import { useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from '@/components/ui-v2';

export interface DemoteOrgAdminWorkspace {
    _id: string;
    name: string;
}

export interface DemoteOrgAdminRole {
    _id: string;
    name: string;
    scope: string;
}

export interface DemoteOrgAdminModalProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (fallbackWorkspaceRoles: { workspaceId: string; roleId: string }[]) => void;
    memberName: string;
    orgName: string;
    workspaces: DemoteOrgAdminWorkspace[];
    roles: DemoteOrgAdminRole[];
    submitting?: boolean;
}

export default function DemoteOrgAdminModal({
    open,
    onClose,
    onConfirm,
    memberName,
    orgName,
    workspaces,
    roles,
    submitting,
}: DemoteOrgAdminModalProps) {
    const wsRoles = roles.filter((r) => r.scope === 'workspace');
    const [picks, setPicks] = useState<Record<string, string>>({});

    const setPick = (wsId: string, roleId: string) => {
        setPicks((p) => ({ ...p, [wsId]: roleId }));
    };

    const handleConfirm = () => {
        const fallback = Object.entries(picks)
            .filter(([, roleId]) => roleId)
            .map(([workspaceId, roleId]) => ({ workspaceId, roleId }));
        onConfirm(fallback);
    };

    return (
        <Modal open={open} onClose={onClose} width={520}>
            <ModalHeader onClose={onClose}>Remove organization admin</ModalHeader>
            <ModalBody>
                <p className="text-sm text-gray-800 mb-3">
                    Remove <span className="font-medium">{memberName}</span>'s organization admin status in{' '}
                    <span className="font-medium">{orgName}</span>?
                </p>
                <p className="text-xs text-gray-600 mb-4">
                    They'll lose access to all workspaces unless you assign them a workspace role below. Skip a workspace
                    to leave them with no access there.
                </p>

                <div className="space-y-2 max-h-64 overflow-y-auto border rounded-md divide-y divide-gray-100">
                    {workspaces.map((ws) => (
                        <div key={ws._id} className="flex items-center justify-between px-3 py-2">
                            <span className="text-sm text-gray-800">{ws.name}</span>
                            <select
                                value={picks[ws._id] || ''}
                                onChange={(e) => setPick(ws._id, e.target.value)}
                                className="border border-gray-300 rounded px-2 py-1 text-xs"
                            >
                                <option value="">— No access —</option>
                                {wsRoles.map((r) => (
                                    <option key={r._id} value={r._id}>
                                        {r.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    ))}
                </div>

                <p className="text-xs text-gray-500 mt-3">{memberName} will receive an email about this change.</p>
            </ModalBody>
            <ModalFooter>
                <Button variant="secondary" onClick={onClose} disabled={submitting}>
                    Cancel
                </Button>
                <Button variant="danger" onClick={handleConfirm} disabled={submitting}>
                    {submitting ? 'Saving…' : 'Remove org admin'}
                </Button>
            </ModalFooter>
        </Modal>
    );
}
